import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Grid
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TagIcon from '@mui/icons-material/Tag';
import SearchLimitBanner from './SearchLimitBanner';
import TikTokEmbed from './TikTokEmbed';
import { generateContentStrategy } from '../services/api';
import { incrementSearchCount, hasReachedLimit, isPremiumUser } from '../services/searchLimit';

const ContentStrategy = () => {
  const [topic, setTopic] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [contentIdeas, setContentIdeas] = useState([]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!topic.trim()) {
      setError('Please enter a topic');
      return;
    }

    // Check search limit
    if (hasReachedLimit()) {
      setError('You have reached your search limit. Please upgrade to premium or enter your premium password.');
      return;
    }

    setLoading(true);
    setError(null);
    setContentIdeas([]);

    try {
      // Increment search count
      incrementSearchCount();

      const response = await generateContentStrategy(topic);

      // Store in window for debugging
      window.lastResponse = response;

      console.log('🔍 Full response:', response);
      console.log('🔍 Ideas array:', response.ideas);
      console.log('🔍 Full response as JSON:', JSON.stringify(response, null, 2));

      if (response.ideas && response.ideas.length > 0) {
        console.log('🔍 First idea:', response.ideas[0]);
        console.log('🔍 First idea as JSON:', JSON.stringify(response.ideas[0], null, 2));
        console.log('🔍 First idea has trendingVideos?', 'trendingVideos' in response.ideas[0]);
        console.log('🔍 First idea trending videos:', response.ideas[0].trendingVideos);

        if (response.ideas[0].trendingVideos) {
          console.log('🔍 Number of trending videos:', response.ideas[0].trendingVideos.length);
        }
      }

      setContentIdeas(response.ideas || []);
    } catch (err) {
      console.error('Error generating content strategy:', err);
      setError(err.response?.data?.message || 'Failed to generate content strategy. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const premium = isPremiumUser();
  const maxIdeas = premium ? 10 : 3;

  return (
    <Box>
      <SearchLimitBanner />

      <Paper
        elevation={0}
        sx={{
          p: 4,
          mb: 4,
          background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(236, 72, 153, 0.1) 100%)',
          borderRadius: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <AutoAwesomeIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              AI Content Strategy Generator
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
              Get personalized content ideas based on trending TikTok data
            </Typography>
          </Box>
        </Box>

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
          <TextField
            fullWidth
            label="What topic do you want to create content about?"
            placeholder="e.g., fitness, cooking, travel, tech reviews"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            disabled={loading}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                background: 'white',
              },
            }}
          />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              You'll receive {maxIdeas} {premium ? 'premium' : 'free'} content ideas
            </Typography>
            <Button
              type="submit"
              variant="contained"
              disabled={loading || !topic.trim()}
              startIcon={loading ? <CircularProgress size={20} /> : <AutoAwesomeIcon />}
              sx={{
                px: 4,
                py: 1.5,
              }}
            >
              {loading ? 'Generating...' : 'Generate Ideas'}
            </Button>
          </Box>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {contentIdeas.length > 0 && (
        <Box>
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
            Your Content Ideas
          </Typography>

          <Grid container spacing={3}>
            {contentIdeas.map((idea, index) => (
              <Grid item xs={12} key={index}>
                <Card
                  sx={{
                    borderRadius: 3,
                    boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.08)',
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Chip
                        label={`Idea ${index + 1}`}
                        sx={{
                          background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
                          color: 'white',
                          fontWeight: 600,
                        }}
                      />
                    </Box>

                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      {idea.title}
                    </Typography>

                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                      {idea.description}
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    {idea.caption && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                          Suggested Caption:
                        </Typography>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            background: 'rgba(139, 92, 246, 0.05)',
                            borderRadius: 2,
                          }}
                        >
                          <Typography variant="body2">{idea.caption}</Typography>
                        </Paper>
                      </Box>
                    )}

                    {idea.hashtags && idea.hashtags.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <TagIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            Trending Hashtags:
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {idea.hashtags.map((tag, i) => (
                            <Chip
                              key={i}
                              label={tag}
                              size="small"
                              sx={{ background: 'rgba(139, 92, 246, 0.1)' }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}

                    {idea.bestTime && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <AccessTimeIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          <strong>Best time to post:</strong> {idea.bestTime}
                        </Typography>
                      </Box>
                    )}

                    {idea.trendingVideos && idea.trendingVideos.length > 0 ? (
                      <Box sx={{ mt: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                          <TrendingUpIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            Trending Videos That Inspired This Idea: ({idea.trendingVideos.length} videos)
                          </Typography>
                        </Box>
                        <Grid container spacing={2}>
                          {idea.trendingVideos.slice(0, 3).map((video, i) => {
                            console.log(`Rendering video ${i}:`, video);
                            return (
                              <Grid item xs={12} sm={6} md={4} key={i}>
                                <TikTokEmbed
                                  videoUrl={video.url || video.video_url}
                                  description={video.description || video.text}
                                  video={video}
                                />
                              </Grid>
                            );
                          })}
                        </Grid>
                      </Box>
                    ) : (
                      <Box sx={{ mt: 3, p: 2, background: 'rgba(255, 193, 7, 0.1)', borderRadius: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          ⚠️ No trending videos found for this idea.
                          {console.log('Trending videos missing for idea:', idea.title)}
                          {console.log('idea.trendingVideos:', idea.trendingVideos)}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={60} />
            <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
              Analyzing trending content and generating ideas...
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ContentStrategy;
